import React, { useState } from 'react';
import { Box, Dialog, IconButton, Typography, Grid } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';

/**
 * Intrinsic pixel sizes of the assets in /public/screens, so the browser can
 * reserve space before the image loads (no layout shift). Anything not listed
 * falls back to a 1920x1200 full-page capture.
 */
const SIZES = {
  'ai-analysis': [1456, 1018],
  'ai-report': [1456, 1500],
  'ask-ai': [704, 616],
  'attack-timeline': [1456, 1900],
  'daily-severity': [1332, 686],
  'mitre-heatmap': [1456, 444],
  'needs-attention': [1080, 1610],
  'recent-detections': [1080, 1610],
  sla: [704, 808],
  'threat-intel': [1456, 1700],
  'top-techniques': [552, 1436],
  'vendor-alert': [1456, 978],
};

const sizeFor = (src) => {
  const name = String(src).split('/').pop().replace(/\.[a-z0-9]+$/i, '');
  return SIZES[name] || [1920, 1200];
};

const BrowserBar = () => (
  <Box
    aria-hidden="true"
    sx={{
      display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, height: 28, flexShrink: 0,
      bgcolor: (t) => (t.palette.mode === 'dark' ? 'grey.800' : 'grey.100'),
      borderBottom: 1, borderColor: 'divider',
    }}
  >
    {['#ff5f57', '#febc2e', '#28c840'].map((c) => (
      <Box key={c} sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c }} />
    ))}
    <Box sx={{ flex: 1, mx: 2, height: 14, maxWidth: 320, borderRadius: 1, bgcolor: (t) => (t.palette.mode === 'dark' ? 'grey.700' : 'grey.200') }} />
  </Box>
);

/**
 * A product screenshot in a browser-chrome or card frame, with click-to-enlarge.
 *
 * Props:
 *  - src, alt (required), caption (shown in the lightbox, and under the frame when showCaption)
 *  - frame: 'browser' | 'card' | 'none'
 *  - maxHeight: crop tall images to this height (top-aligned, with a fade); the lightbox shows the full image
 *  - aspectRatio: show the image in a fixed-ratio window (e.g. '4 / 3'); fit 'cover' (top-aligned crop) or 'contain'
 *  - position: CSS object-position for the windowed image (default top center for cover)
 *  - eager: load immediately instead of lazily (use for the hero image)
 */
const Screenshot = ({
  src, alt, caption, frame = 'browser', maxHeight, aspectRatio, fit = 'cover', position, eager = false, showCaption = false, sx, imgSx,
}) => {
  const [open, setOpen] = useState(false);
  const [w, h] = sizeFor(src);
  const radius = frame === 'none' ? 1 : 2;
  const windowed = Boolean(aspectRatio);
  const fade = Boolean(maxHeight) || (windowed && fit === 'cover');

  return (
    <Box component="figure" sx={{ m: 0, minWidth: 0, ...sx }}>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Enlarge screenshot: ${alt}`}
        sx={{
          display: 'flex', flexDirection: 'column', width: '100%', p: 0, m: 0, font: 'inherit', textAlign: 'left',
          cursor: 'zoom-in', position: 'relative', overflow: 'hidden', borderRadius: radius,
          bgcolor: 'background.paper',
          border: frame === 'none' ? 0 : 1, borderColor: 'divider',
          boxShadow: frame === 'none' ? 0 : '0 18px 40px -12px rgba(15, 23, 42, 0.28), 0 4px 12px -4px rgba(15, 23, 42, 0.12)',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': frame === 'none' ? {} : { transform: 'translateY(-3px)', boxShadow: '0 24px 48px -12px rgba(15, 23, 42, 0.35)' },
          '&:hover .zoom-hint, &:focus-visible .zoom-hint': { opacity: 1 },
          '&:focus-visible': { outline: '3px solid', outlineColor: 'secondary.main', outlineOffset: 2 },
        }}
      >
        {frame === 'browser' && <BrowserBar />}
        <Box
          sx={{
            position: 'relative', overflow: 'hidden', width: '100%',
            maxHeight: maxHeight || 'none',
            ...(windowed && { aspectRatio, bgcolor: (t) => (t.palette.mode === 'dark' ? 'grey.900' : 'grey.50') }),
          }}
        >
          <Box
            component="img"
            src={src}
            alt={alt}
            width={w}
            height={h}
            loading={eager ? 'eager' : 'lazy'}
            decoding="async"
            sx={{
              display: 'block', width: '100%', height: 'auto',
              ...(windowed && {
                height: '100%', objectFit: fit, objectPosition: position || (fit === 'cover' ? 'top center' : 'center'),
                ...(fit === 'contain' && { p: 1.5, boxSizing: 'border-box' }),
              }),
              ...imgSx,
            }}
          />
          {fade && (
            <Box
              aria-hidden="true"
              sx={{
                position: 'absolute', left: 0, right: 0, bottom: 0, height: 72, pointerEvents: 'none',
                background: (t) => `linear-gradient(to bottom, rgba(0,0,0,0), ${t.palette.background.paper})`,
              }}
            />
          )}
        </Box>
        <Box
          className="zoom-hint"
          aria-hidden="true"
          sx={{
            position: 'absolute', right: 10, bottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 34, height: 34, borderRadius: '50%', bgcolor: 'rgba(15,23,42,0.72)', color: 'common.white',
            opacity: 0, transition: 'opacity 0.2s',
          }}
        >
          <ZoomInIcon fontSize="small" />
        </Box>
      </Box>
      {showCaption && caption && (
        <Typography component="figcaption" variant="body2" color="text.secondary" sx={{ mt: 1.5, textAlign: 'center' }}>
          {caption}
        </Typography>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xl"
        fullWidth
        aria-label={alt}
        slotProps={{ backdrop: { sx: { bgcolor: 'rgba(5, 8, 15, 0.88)' } } }}
        PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none', m: { xs: 1, sm: 3 }, maxHeight: 'calc(100% - 32px)', overflow: 'visible' } }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: 0 }}>
          <Box sx={{ position: 'relative', width: '100%', maxWidth: Math.round((w / 2) * 1.25) }}>
            <IconButton
              onClick={() => setOpen(false)}
              aria-label="Close screenshot"
              sx={{
                position: 'absolute', top: { xs: 6, sm: -14 }, right: { xs: 6, sm: -14 }, zIndex: 1, boxShadow: 3,
                bgcolor: 'rgba(255,255,255,0.95)', color: 'grey.900', '&:hover': { bgcolor: 'common.white' },
              }}
            >
              <CloseIcon />
            </IconButton>
            <Box sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 140px)', borderRadius: 2, bgcolor: 'background.paper' }}>
              <Box component="img" src={src} alt={alt} width={w} height={h} sx={{ display: 'block', width: '100%', height: 'auto' }} />
            </Box>
          </Box>
          {caption && (
            <Typography variant="body1" sx={{ color: 'grey.100', mt: 2, textAlign: 'center', maxWidth: 900, px: 2 }}>
              {caption}
            </Typography>
          )}
        </Box>
      </Dialog>
    </Box>
  );
};

/**
 * Titled screenshot cards in a responsive grid.
 * items: [{ src, alt, title, caption, frame?, fit?, position? }] — every image is shown in the same aspect-ratio window.
 */
export const ScreenshotGrid = ({ items, columns = { xs: 12, sm: 6, md: 4 }, aspectRatio = '4 / 3', frame = 'card' }) => (
  <Grid container spacing={4}>
    {items.map((item) => (
      <Grid item {...columns} key={item.src}>
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Screenshot
            src={item.src}
            alt={item.alt}
            caption={item.caption}
            frame={item.frame || frame}
            aspectRatio={aspectRatio}
            fit={item.fit || 'cover'}
            position={item.position}
          />
          <Typography variant="subtitle1" component="h3" fontWeight="bold" sx={{ mt: 2 }}>
            {item.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {item.caption}
          </Typography>
        </Box>
      </Grid>
    ))}
  </Grid>
);

export default Screenshot;
